import { useParams, Link } from 'react-router-dom';
import { articles, articleContents } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/home';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const content = articleContents[article.id] || `<p>${article.excerpt}</p>`;
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.domain}/` },
      { '@type': 'ListItem', position: 2, name: article.category, item: `${siteInfo.domain}/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Breadcrumb
        items={[
          { name: '首页', path: '/' },
          { name: article.category, path: `/category/${article.categorySlug}` },
          { name: article.title },
        ]}
      />

      <article className="w-full max-w-[900px] mx-auto px-4 md:px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Link
              to={`/category/${article.categorySlug}`}
              className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
            >
              {article.category}
            </Link>
            <span className="text-xs text-foreground-400">{article.publishDate}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-400">阅读时间 {article.readTime}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-400">作者 {article.author}</span>
          </div>

          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-2 flex-wrap">
            {article.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-0.5 bg-background-100 text-secondary-600 text-xs rounded-md">
                #{tag}
              </span>
            ))}
          </div>
        </div>

        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-10">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - ${siteInfo.name}`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div
          className="article-content prose-custom"
          dangerouslySetInnerHTML={{ __html: content }}
        />

        <div className="mt-12 pt-8 border-t border-background-200/70">
          <a
            href={siteInfo.downloadLinks.android}
            className="block w-full bg-gradient-to-r from-primary-500 to-accent-500 text-background-50 rounded-2xl p-6 text-center hover:from-primary-600 hover:to-accent-600 transition-all cursor-pointer"
          >
            <p className="font-heading text-lg font-semibold mb-1">准备好体验了吗？</p>
            <p className="text-sm text-background-50/80">立即下载008pg国际App，开启精彩体验</p>
          </a>
        </div>

        {relatedArticles.length > 0 && (
          <div className="mt-14">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={ra.coverImage}
                      alt={ra.title}
                      title={`${ra.title} - ${siteInfo.name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}